import React, { useState, useEffect } from "react";
import axios from "axios";
import ChatHistory from "./chat-history";
import ChatHeader from "./chat-header";
import ChatMessages from "./chat-messages";
import ChatInput from "./chat-input";
import { Link } from "react-router-dom";
import "./chat-bot.css"; // Import your CSS styles for the chat bot

const ChatBot = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [currentChatId, setCurrentChatId] = useState(null);

  const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

  // Khôi phục lịch sử chat và trạng thái chat hiện tại khi tải trang
  useEffect(() => {
    const savedChats = localStorage.getItem("chatHistory");
    const savedCurrentChat = localStorage.getItem("currentChat");
    if (savedChats) {
      setChatHistory(JSON.parse(savedChats));
    }
    if (savedCurrentChat) {
      const { id, messages } = JSON.parse(savedCurrentChat);
      setCurrentChatId(id);
      setMessages(messages);
    }
  }, []);

  // Lưu lịch sử chat và chat hiện tại vào localStorage khi thay đổi
  useEffect(() => {
    localStorage.setItem("chatHistory", JSON.stringify(chatHistory));
    if (messages.length > 0) {
      const currentChat = {
        id: currentChatId || `chat-${Date.now()}`,
        messages,
      };
      localStorage.setItem("currentChat", JSON.stringify(currentChat));
    } else {
      localStorage.removeItem("currentChat");
    }
  }, [chatHistory, messages, currentChatId]);

  const toggleDropdown = (messageId) => {
    setDropdownOpen((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  const handleCopy = (text, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    alert("Đã sao chép tin nhắn!");
  };

  const handleEdit = (messageId, e) => {
    e.stopPropagation();
    const message = messages.find((msg) => msg.id === messageId);
    if (message) {
      setInputMessage(message.text);
      setEditingMessageId(messageId);
    }
  };

  const handleNewChat = () => {
    // Chỉ lưu vào lịch sử nếu messages là từ phiên chat mới
    if (
      messages.length > 0 &&
      messages.some((msg) => msg.text.trim() !== "") &&
      (!currentChatId || !chatHistory.some((chat) => chat.id === currentChatId))
    ) {
      const chatId = currentChatId || `chat-${Date.now()}`;
      const firstValidMessage = messages.find(
        (msg) => msg.text.trim().length > 10
      );
      const chatSummary = firstValidMessage
        ? firstValidMessage.text.slice(0, 30) + "..."
        : "Cuộc trò chuyện mới";
      const newChat = {
        id: chatId,
        summary: chatSummary,
        messages: [...messages],
        timestamp: new Date().toLocaleString(),
      };
      setChatHistory((prev) => [newChat, ...prev]);
    }
    // Reset trạng thái
    setMessages([]);
    setDropdownOpen({});
    setInputMessage("");
    setEditingMessageId(null);
    setCurrentChatId(null);
    localStorage.removeItem("currentChat");
  };

  const handleLoadChat = (chatId) => {
    const selectedChat = chatHistory.find((chat) => chat.id === chatId);
    if (selectedChat) {
      setMessages([...selectedChat.messages]);
      setDropdownOpen({});
      setInputMessage("");
      setEditingMessageId(null);
      setCurrentChatId(chatId);
      localStorage.setItem(
        "currentChat",
        JSON.stringify({ id: chatId, messages: selectedChat.messages })
      );
    }
  };

  const handleDeleteChat = (chatId) => {
    // Xóa cuộc trò chuyện khỏi lịch sử
    setChatHistory((prev) => prev.filter((chat) => chat.id !== chatId));
    // Nếu cuộc trò chuyện đang hiển thị bị xóa, reset trạng thái
    if (currentChatId === chatId) {
      setMessages([]);
      setDropdownOpen({});
      setInputMessage("");
      setEditingMessageId(null);
      setCurrentChatId(null);
      localStorage.removeItem("currentChat");
    }
  };

  const formatMessageToHtml = (text) => {
    let formattedText = text
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\* (.*?)(?:\n|$)/g, "<li>$1</li>")
      .replace(/\n/g, "<br>");

    if (formattedText.includes("<li>")) {
      formattedText = formattedText.replace(/(<li>.*<\/li>)/g, "<ul>$1</ul>");
    }

    return formattedText;
  };

  const sendMessageToGemini = async (message) => {
    try {
      const prompt = `
        Bạn là một trợ lý du lịch chuyên nghiệp. Tôi muốn bạn tạo một lịch trình du lịch chi tiết cho ${message}, theo phong cách chậm rãi, tập trung vào chụp ảnh và khám phá thiên nhiên. Hãy định dạng lịch trình theo cấu trúc sau:

        **🗓️ LỊCH TRÌNH DU LỊCH [ĐỊA ĐIỂM] [SỐ NGÀY] NGÀY – PHONG CÁCH CHẬM RÃI, CHỤP ẢNH, KHÁM PHÁ THIÊN NHIÊN**

        **Ngày [X] – [Mô tả ngắn về ngày]**
        *Sáng:*
        - [Hoạt động sáng, ví dụ: di chuyển, tham quan, chụp ảnh...]
        - [Gợi ý ăn sáng hoặc địa điểm cụ thể]

        *Trưa:*
        - [Hoạt động trưa, ví dụ: ăn trưa, nghỉ ngơi...]
        - [Gợi ý quán ăn hoặc hoạt động]

        *Chiều:*
        - [Hoạt động chiều, ví dụ: tham quan, chụp ảnh...]
        - [Gợi ý địa điểm hoặc hoạt động]

        *Tối:*
        - [Hoạt động tối, ví dụ: ăn tối, thư giãn...]
        - [Gợi ý quán ăn, cà phê, hoặc hoạt động]

        **[Tiếp tục cho các ngày khác]**

        **✨ Gợi ý thêm:**
        - [Gợi ý di chuyển, chi phí dự kiến, trang bị cần thiết...]

        Hãy đảm bảo câu trả lời chi tiết, sử dụng ngôn ngữ tự nhiên, thân thiện, và phù hợp với người dùng yêu thích chụp ảnh, thiên nhiên, và trải nghiệm chậm rãi. Trả lời bằng tiếng Việt và sử dụng biểu tượng cảm xúc (emoji) như 🗓️, ✨ để tăng tính sinh động.
      `;

      const response = await axios.post(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${GEMINI_API_KEY}`,
        {
          contents: [
            {
              parts: [
                {
                  text: prompt,
                },
              ],
            },
          ],
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      return response.data.candidates[0].content.parts[0].text;
    } catch (error) {
      console.error(
        "Lỗi khi gọi API Gemini:",
        error.response?.data || error.message
      );
      return "Tôi là Chat Bot AI chỉ hỗ trợ các câu hỏi liên quan đến du lịch!";
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    setIsLoading(true);

    if (editingMessageId) {
      // Cập nhật tin nhắn đã chỉnh sửa
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === editingMessageId
            ? {
                ...msg,
                text: inputMessage,
                timestamp: new Date().toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
              }
            : msg
        )
      );
      setInputMessage("");
      setEditingMessageId(null);
      setIsLoading(false);
    } else {
      // Gửi tin nhắn mới
      const newUserMessage = {
        id: `sent-${Date.now()}`,
        text: inputMessage,
        type: "sent",
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      setMessages((prev) => [...prev, newUserMessage]);
      setInputMessage("");

      const aiResponse = await sendMessageToGemini(inputMessage);

      const newAiMessage = {
        id: `received-${Date.now()}`,
        text: aiResponse,
        type: "received",
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      setMessages((prev) => {
        const updatedMessages = [...prev, newAiMessage];
        // Lưu vào lịch sử chat ngay sau khi thêm tin nhắn
        if (updatedMessages.some((msg) => msg.text.trim() !== "")) {
          const chatId = currentChatId || `chat-${Date.now()}`;
          const firstValidMessage = updatedMessages.find(
            (msg) => msg.text.trim().length > 10
          );
          const chatSummary = firstValidMessage
            ? firstValidMessage.text.slice(0, 30) + "..."
            : "Cuộc trò chuyện mới";
          const newChat = {
            id: chatId,
            summary: chatSummary,
            messages: updatedMessages,
            timestamp: new Date().toLocaleString(),
          };
          setChatHistory((prevHistory) => {
            const updatedHistory = prevHistory.filter(
              (chat) => chat.id !== chatId
            );
            return [newChat, ...updatedHistory];
          });
          setCurrentChatId(chatId);
        }
        return updatedMessages;
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="content">
      <div className="container">
        <div className="customer-chat">
          <div className="header-navigation">
            <Link to="/" className="d-inline-flex align-items-center">
              <i className="isax isax-arrow-left me-2"></i>Trang chủ
            </Link>
          </div>
          <div className="row chat-window">
            <ChatHistory
              chatHistory={chatHistory}
              handleLoadChat={handleLoadChat}
              handleNewChat={handleNewChat}
              handleDeleteChat={handleDeleteChat}
            />
            <div className="col-lg-9 chat-cont-right chat-window-long">
              <div className="card chat-window mb-0 shadow-none flex-fill">
                <ChatHeader />
                <ChatMessages
                  messages={messages}
                  isLoading={isLoading}
                  formatMessageToHtml={formatMessageToHtml}
                  toggleDropdown={toggleDropdown}
                  dropdownOpen={dropdownOpen}
                  handleCopy={handleCopy}
                  handleEdit={handleEdit}
                />
                <ChatInput
                  inputMessage={inputMessage}
                  setInputMessage={setInputMessage}
                  handleSendMessage={handleSendMessage}
                  isLoading={isLoading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatBot;
